import { TableauViz } from '@tableau/embedding-api';
import { AppConfig } from 'AppConfig';
import axios from 'axios';
import 'common/report.css';
import debounce from 'lodash.debounce';
import ReportRefreshOverlay from 'features/frontoffice/component/reportRefreshOverlay';
import {
  buildTableauRuntimeFilterPlan,
  buildTableauVizFilters,
  getDateBasisNotAppliedLabel,
  getDateBasisNotAppliedValueForTableauParameters,
  getTableauPersonIdValues,
  getTableauRuntimeFilterKey,
  getTableauVizFilterFields,
  TABLEAU_FILTER_FIELDS,
  TABLEAU_RUNTIME_TARGETS
} from 'features/frontoffice/component/reportTableauFilters';
import { setNotAppliedFilters, setTableauAppliedFilterValues } from 'features/globalFilters/globalFiltersReducer';
import {
  getTableauFieldAliases,
  getTableauParameterNameCandidates,
  getTableauParameterValueCandidates,
  normalizeTableauName
} from 'features/frontoffice/component/reportTableauRuntime';
import {
  applyFilterAcrossWorksheets,
  clearFilterAcrossWorksheets,
  ensureActiveSheetCanApply,
  ensureActiveSheetCanClear
} from 'features/frontoffice/component/reportTableauApply';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import * as AppActionTypes from 'store/actions/appstate';
import { store } from 'store/store';
import { catchTableauReqCancel } from 'utils/catchTableauReqCancel';
import getUrlTicket from 'utils/getUrlTicket';
import { safeDisposeViz } from 'utils/tableauUtils';
/*
  The process of workbook assumes that one workbook has multiple dashborads and
  all filters inside one dashborad have to have their unique name, so users could
  operate on dashboard as atomic unit.
*/

// fo-initial proxy will return 200 even if fo-initial request to tableau server returns 503 
const STRING_503_RESPONSE = "503 Service Temporarily Unavailable";

const is503Error = (data) =>{
  return data.includes(STRING_503_RESPONSE);
};

const getWorkbookName = (workbook) =>
  typeof workbook?.getName === 'function' ? workbook.getName() : (workbook?.name ?? '');

let AD_HOC_TAB_OPEN = false;
let JUST_ENTER = true;

const FILTERS_TO_CHECK = {
  [TABLEAU_FILTER_FIELDS.divisionLabels]: 'Division',
  [TABLEAU_FILTER_FIELDS.personId]: 'Person ID',
  [TABLEAU_FILTER_FIELDS.planLabels]: 'Plan',
};

const RUNTIME_FILTER_DEBOUNCE_MS = 200;

const TABLEAU_FIELD_ALIAS_KEYS_CACHE = new Map();
const getTableauFieldAliasKeys = (fieldName) => {
  let cached = TABLEAU_FIELD_ALIAS_KEYS_CACHE.get(fieldName);
  if (!cached) {
    cached = getTableauFieldAliases(fieldName).map(normalizeTableauName);
    TABLEAU_FIELD_ALIAS_KEYS_CACHE.set(fieldName, cached);
  }
  return cached;
};

const getRuntimeOpSignature = (operation) => {
  if (operation.target === TABLEAU_RUNTIME_TARGETS.parameter) {
    return `p:${String(operation.value ?? '')}`;
  }
  return `f:${(operation.values || []).join('\u0001')}`;
};

const Report = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const tableauVizRef = useRef(null);
  const { tableauRuntimeFilterPlan, runtimeFilterKey, tableauVizFilters, tableauVizFilterFieldsInUrl, hasAsyncFilterWork } = useMemo(() => {
    const vizFilters = buildTableauVizFilters(props.appliedGlobalFilters);
    const fieldsInUrl = getTableauVizFilterFields(vizFilters);
    const runtimePlan = buildTableauRuntimeFilterPlan(props.appliedGlobalFilters);

    const hasAsync = runtimePlan.some((op) => (
      op.target === TABLEAU_RUNTIME_TARGETS.filter
      && Array.isArray(op.values)
      && op.values.length > 0
      && !fieldsInUrl.has(op.field)
    ));
    return {
      tableauVizFilters: vizFilters,
      tableauVizFilterFieldsInUrl: fieldsInUrl,
      tableauRuntimeFilterPlan: runtimePlan,
      runtimeFilterKey: getTableauRuntimeFilterKey(props.appliedGlobalFilters),
      hasAsyncFilterWork: hasAsync
    };
  }, [props.appliedGlobalFilters]);
  const [state, setState] = useState({
    currentDashboard: null,
    dashboards: null,
    insightUrl: null,
    leftPanelOpen: false,
    showInsight: false,
    vizWidth: 0,
    workbook: null,
    workbookTitle: '',
    viz: null,
    reportUrl: null
  });
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);

  const vizContainerDiv = useRef();
  const createTableauRequestRef = useRef(null);
  const tableauReloadRequestIdRef = useRef(0);
  const lastAppliedRuntimeFilterKeyRef = useRef(null);
  const lastLoadedReportSourceRef = useRef(null);
  const isApplyingRuntimeFiltersRef = useRef(false);
  const requestedRuntimeFilterKeyRef = useRef(null);
  const pendingRuntimeFilterKeyRef = useRef(null);
  const latestApplyRuntimeFiltersRef = useRef(null);
  const latestAppliedGlobalFiltersRef = useRef(props.appliedGlobalFilters);
  const tableauMetadataCacheRef = useRef(null);
  const lastAppliedOpSignaturesRef = useRef(new Map());
  const successfulParameterComboRef = useRef(new Map());

  useLayoutEffect(() => {
    latestAppliedGlobalFiltersRef.current = props.appliedGlobalFilters;
  }, [props.appliedGlobalFilters]);

  const updateState = (nextState) => {
    setState((currentState) => ({
      ...currentState,
      ...nextState
    }));
  };

  const invalidateTableauMetadataCache = useCallback(() => {
    tableauMetadataCacheRef.current = null;
    lastAppliedOpSignaturesRef.current = new Map();
    successfulParameterComboRef.current = new Map();
  }, []);

  const clearTableauMetadataCacheOnly = useCallback(() => {
    tableauMetadataCacheRef.current = null;
  }, []);

  const getTableauMetadata = useCallback(async (viz) => {
    const workbook = viz?.workbook;
    const activeSheet = workbook?.activeSheet || workbook?.getActiveSheet?.();

    if (!workbook || !activeSheet) {
      return null;
    }

    const matchesCurrentCache = () => (
      tableauMetadataCacheRef.current?.workbook === workbook
      && tableauMetadataCacheRef.current?.activeSheet === activeSheet
    );

    if (matchesCurrentCache()) {
      return tableauMetadataCacheRef.current.promise;
    }

    const metadataPromise = (async () => {
      const [parameters, filters] = await Promise.all([
        typeof workbook?.getParametersAsync === 'function'
          ? workbook.getParametersAsync()
          : Promise.resolve([]),
        typeof activeSheet.getFiltersAsync === 'function'
          ? activeSheet.getFiltersAsync()
          : Promise.resolve([])
      ]);

      const parameterByName = new Map();
      const filterFieldByName = new Map();

      parameters.forEach((parameter) => {
        if (parameter?.name) {
          parameterByName.set(normalizeTableauName(parameter.name), parameter);
        }
      });

      filters.forEach((filter) => {
        if (filter?.fieldName) {
          filterFieldByName.set(normalizeTableauName(filter.fieldName), filter.fieldName);
        }
      });

      return {
        activeSheet,
        filterFieldByName,
        filters,
        parameterByName,
        parameters,
        workbook
      };
    })();

    tableauMetadataCacheRef.current = {
      activeSheet,
      promise: metadataPromise,
      workbook
    };

    metadataPromise.catch(() => {
      if (matchesCurrentCache()) {
        tableauMetadataCacheRef.current = null;
      }
    });

    return metadataPromise;
  }, []);

  const applyRuntimeFilters = useCallback(async (viz, skipFields) => {
    const metadata = await getTableauMetadata(viz);

    if (!metadata) {
      return;
    }

    const {
      activeSheet,
      filterFieldByName,
      parameterByName,
      parameters,
      workbook
    } = metadata;

    const signatureCache = lastAppliedOpSignaturesRef.current;
    const parameterComboCache = successfulParameterComboRef.current;

    // Build the list of ops we actually need to apply this pass. Skip:
    //  (a) filter ops whose field was already pushed via <viz-filter> in the URL
    //  (b) any op whose signature matches what we last successfully applied
    // The remaining list is what we send to Tableau.
    const opsToApply = [];
    for (const operation of tableauRuntimeFilterPlan) {
      if (
        operation.target === TABLEAU_RUNTIME_TARGETS.filter
        && skipFields?.has(operation.field)
      ) {
        signatureCache.set(operation.field, getRuntimeOpSignature(operation));
        continue;
      }

      const signature = getRuntimeOpSignature(operation);
      if (signatureCache.get(operation.field) === signature) {
        continue;
      }

      opsToApply.push({ operation, signature });
    }

    if (opsToApply.length === 0) {
      return;
    }

    const applyOne = async ({ operation, signature }) => {
      try {
        const aliasKeys = getTableauFieldAliasKeys(operation.field);

        if (operation.target === TABLEAU_RUNTIME_TARGETS.parameter) {
          const parameter = aliasKeys
            .map((aliasKey) => parameterByName.get(aliasKey))
            .find(Boolean);

          if (operation.value === null || operation.value === undefined || operation.value === '') {
            signatureCache.set(operation.field, signature);
            return;
          }

          // Fast path: we've already proven a working (name, value-template)
          // combo for this field on the current workbook. Try it first.
          const cachedCombo = parameterComboCache.get(operation.field);
          if (cachedCombo) {
            const cachedValues = getTableauParameterValueCandidates(
              operation.field, operation.value, parameter
            );
            const candidateValue = cachedValues[cachedCombo.valueIndex] ?? cachedValues[0];
            try {
              await workbook.changeParameterValueAsync(cachedCombo.name, candidateValue);
              signatureCache.set(operation.field, signature);
              return;
            } catch (cachedError) {
              // Fall through to the full retry path below; the cached combo
              // may have gone stale (e.g. workbook structure changed).
              parameterComboCache.delete(operation.field);
            }
          }

          const parameterValues = getTableauParameterValueCandidates(operation.field, operation.value, parameter);
          // Restrict the candidate names to parameters that actually exist on this workbook.
          const allParameterNames = getTableauParameterNameCandidates(operation.field, parameters);
          const parameterNames = allParameterNames
            .map((parameterName) => parameterByName.get(normalizeTableauName(parameterName))?.name)
            .filter((parameterName, index, names) => parameterName && names.indexOf(parameterName) === index);

          // No real parameter matches this field on the current workbook —
          // skip entirely rather than brute-forcing non-existent names.
          if (parameterNames.length === 0) {
            signatureCache.set(operation.field, signature);
            return;
          }

          let lastParameterError;
          let parameterWasApplied = false;

          for (const parameterName of parameterNames) {
            for (let valueIndex = 0; valueIndex < parameterValues.length; valueIndex++) {
              try {
                await workbook.changeParameterValueAsync(parameterName, parameterValues[valueIndex]);
                parameterWasApplied = true;
                parameterComboCache.set(operation.field, { name: parameterName, valueIndex });
                break;
              } catch (error) {
                lastParameterError = error;
              }
            }

            if (parameterWasApplied) {
              break;
            }
          }

          if (!parameterWasApplied && lastParameterError) {
            throw lastParameterError;
          }

          signatureCache.set(operation.field, signature);
          return;
        }

        const resolvedFieldName = aliasKeys
          .map((aliasKey) => filterFieldByName.get(aliasKey))
          .find(Boolean) || operation.field;
        const fieldCandidates = [
          resolvedFieldName,
          ...getTableauFieldAliases(operation.field)
        ].filter((fieldName, index, fieldNames) => fieldName && fieldNames.indexOf(fieldName) === index);

        const tryApplyFilter = async (sheet, values) => {
          let lastFilterError;

          for (const fieldName of fieldCandidates) {
            try {
              await sheet.applyFilterAsync(fieldName, values, AppConfig.TABLEAU_UPDATE_REPLACE);
              return true;
            } catch (error) {
              lastFilterError = error;
            }
          }

          if (lastFilterError) {
            throw lastFilterError;
          }

          return false;
        };

        const tryClearFilter = async (sheet) => {
          let lastClearError;

          for (const fieldName of fieldCandidates) {
            try {
              await sheet.clearFilterAsync(fieldName);
              return true;
            } catch (error) {
              lastClearError = error;
            }
          }

          if (lastClearError) {
            throw lastClearError;
          }

          return false;
        };

        const worksheetTargets = Array.isArray(activeSheet?.worksheets)
          ? activeSheet.worksheets
          : null;

        if (!operation.values?.length) {
          // Only issue a clear when this field previously had a value applied.
          // Dashboard sheets do not reliably reflect runtime filter clears at
          // the dashboard level, so fan the operation out to child worksheets.
          if (operation.clearWhenEmpty && signatureCache.has(operation.field)) {
            if (worksheetTargets?.length) {
              let clearedOnDashboard = false;
              try {
                ensureActiveSheetCanClear(activeSheet);
                clearedOnDashboard = await tryClearFilter(activeSheet);
              } catch (error) {
                // Some Tableau dashboards do not expose clearFilterAsync.
                // Continue with worksheet-level clear as the primary path.
                clearedOnDashboard = false;
              }

              await clearFilterAcrossWorksheets({
                worksheetTargets,
                tryClearFilter
              });

              if (!clearedOnDashboard) {
                // No-op: worksheet-level clear succeeded and is authoritative.
              }
            } else {
              ensureActiveSheetCanClear(activeSheet);
              await tryClearFilter(activeSheet);
            }
          }
          signatureCache.set(operation.field, signature);
          return;
        }

        if (worksheetTargets?.length) {
          // Open report views are usually dashboards. Applying list filters to
          // the dashboard object itself can leave embedded banner values stale,
          // while applying to each worksheet keeps the dashboard state in sync.
          await applyFilterAcrossWorksheets({
            worksheetTargets,
            tryApplyFilter,
            values: operation.values
          });
        } else {
          ensureActiveSheetCanApply(activeSheet);
          await tryApplyFilter(activeSheet, operation.values);
        }
        signatureCache.set(operation.field, signature);
      } catch (error) {
        console.log(`Unable to apply Tableau ${operation.target} ${operation.field}:`, error);
        // Do not update the signature cache — failed ops should be retried on
        // the next apply.
      }
    };

    // Pause automatic updates while we batch the apply, then resume to trigger
    // a single re-render.
    const supportsPause =
      typeof viz?.pauseAutomaticUpdatesAsync === 'function'
      && typeof viz?.resumeAutomaticUpdatesAsync === 'function';

    if (supportsPause) {
      try {
        await viz.pauseAutomaticUpdatesAsync();
      } catch (error) {
        console.log('Unable to pause Tableau automatic updates:', error);
      }
    }

    try {
      // Independent ops are fired concurrently. Each applyOne swallows its own
      // errors, so allSettled is functionally equivalent to all() here but
      // makes the intent explicit.
      await Promise.allSettled(opsToApply.map(applyOne));
    } finally {
      if (supportsPause) {
        try {
          await viz.resumeAutomaticUpdatesAsync();
        } catch (error) {
          console.log('Unable to resume Tableau automatic updates:', error);
        }
      }
    }
  }, [getTableauMetadata, props.reportSource, runtimeFilterKey, tableauRuntimeFilterPlan]);

  // Keep ref updated so the deferred-retry path in the runtime filter effect
  // always calls the version of applyRuntimeFilters with the latest filter plan.
  // useLayoutEffect ensures the ref is only set from committed renders and fires
  // synchronously before the runtime-filter useEffect in the same commit.
  useLayoutEffect(() => {
    latestApplyRuntimeFiltersRef.current = applyRuntimeFilters;
  });

  const applyInitialRuntimeFilters = useCallback(async (viz) => {
    try {
      // On initial load, skip runtime re-application of filters that already
      // rode in the viz URL — Tableau has them applied from the GET request.
      await applyRuntimeFilters(viz, tableauVizFilterFieldsInUrl);
    } catch (error) {
      console.log('Unable to apply initial Tableau runtime filters:', error);
    }
  }, [applyRuntimeFilters, tableauVizFilterFieldsInUrl]);

  const appendVizFilters = useCallback((viz) => {
    tableauVizFilters.forEach(({ field, value }) => {
      const vizFilter = document.createElement('viz-filter');
      vizFilter.setAttribute('field', field);
      vizFilter.setAttribute('value', value);
      viz.appendChild(vizFilter);
    });
  }, [props.reportSource, runtimeFilterKey, tableauVizFilters]);

  const detectNotAppliedFilters = useCallback(async (viz) => {
    try {
      const appliedGlobalFiltersSnapshot = latestAppliedGlobalFiltersRef.current || {};
      const metadata = await getTableauMetadata(viz);
      if (!metadata) return;

      const { filters, parameters } = metadata;

      const reportFieldNames = new Set(
        filters
          .map((f) => (typeof f?.fieldName === 'string' ? f.fieldName : ''))
          .filter((name) => name)
          .map((name) => name.toLowerCase())
      );

      const missing = Object.entries(FILTERS_TO_CHECK)
        .filter(([tableauField]) => !reportFieldNames.has(tableauField.toLowerCase()))
        .map(([, label]) => label);

      // If the person_id field exists on the report and the user entered
      // Person IDs, use getDomainAsync() to get all valid Person IDs in the
      // workbook and check how many of the sent IDs actually exist.
      const personIdFieldName = TABLEAU_FILTER_FIELDS.personId.toLowerCase();
      const personIdFieldExists = reportFieldNames.has(personIdFieldName);
      const sentPersonIds = getTableauPersonIdValues(appliedGlobalFiltersSnapshot);
      let matchedPersonIds = [];

      console.log('[Tableau] Person ID check:', {
        fieldExistsOnReport: personIdFieldExists,
        personIdValueFromState: appliedGlobalFiltersSnapshot?.personIdValue,
        sentPersonIdsCount: sentPersonIds.length,
        sentPersonIds: sentPersonIds,
        missingFilters: missing
      });

      if (personIdFieldExists && sentPersonIds.length > 0 && !missing.includes('Person ID')) {
        const personIdFilter = filters.find(
          (f) => (f.fieldName ?? '').toLowerCase() === personIdFieldName
        );

        if (personIdFilter && typeof personIdFilter.getDomainAsync === 'function') {
          try {
            const domain = await personIdFilter.getDomainAsync();
            const domainValues = new Set(
              (domain?.values || []).map((v) => String(v.value ?? v.formattedValue ?? v).trim().toLowerCase())
            );

            matchedPersonIds = sentPersonIds.filter(
              (id) => domainValues.has(String(id).trim().toLowerCase())
            );
            const unmatchedPersonIds = sentPersonIds.filter(
              (id) => !domainValues.has(String(id).trim().toLowerCase())
            );

            console.log(`[Tableau] Person ID domain size: ${domainValues.size}`);
            console.log(`[Tableau] Person IDs sent: ${sentPersonIds.length}, matched: ${matchedPersonIds.length}, unmatched: ${unmatchedPersonIds.length}`);
            if (unmatchedPersonIds.length > 0) {
              console.log('[Tableau] Unmatched Person IDs:', unmatchedPersonIds);
            }

            if (matchedPersonIds.length === 0) {
              missing.push('Person ID');
            }
          } catch (err) {
            console.log('[Tableau] Unable to get person_id domain:', err);
            // If getDomainAsync fails, fall back to treating as applied
            matchedPersonIds = sentPersonIds;
          }
        } else {
          // getDomainAsync not available — fall back to treating as applied
          matchedPersonIds = sentPersonIds;
          console.log(`[Tableau] Person IDs applied (domain check unavailable): ${sentPersonIds.length}`, sentPersonIds);
        }
      }

      const dateBasisNotAppliedValue = getDateBasisNotAppliedValueForTableauParameters(
        appliedGlobalFiltersSnapshot?.dateBasis,
        parameters
      );
      if (dateBasisNotAppliedValue !== null) {
        missing.push(getDateBasisNotAppliedLabel(dateBasisNotAppliedValue));
      }

      store.dispatch(setNotAppliedFilters(missing));

      if (matchedPersonIds.length > 0) {
        store.dispatch(setTableauAppliedFilterValues({ personId: matchedPersonIds }));
      } else {
        store.dispatch(setTableauAppliedFilterValues({ personId: [] }));
      }
    } catch (err) {
      console.log('[FiltersNotApplied] Unable to detect filters:', err);
    }
  }, [getTableauMetadata]);

  const calculateVizWidth = useCallback(() => {
    let frameWidthPercentage = 96;
    var mediaDesktopSmall = window.matchMedia('(max-width: 1600px)');

    if (mediaDesktopSmall.matches) {
      frameWidthPercentage = 93.5;
    }
    if (
      location &&
      location.state &&
      location.state.state &&
      props.category === AppConfig.EXPLORE_CAROUSEL
    ) {
      frameWidthPercentage = frameWidthPercentage - 6;
    }

    if (state.leftPanelOpen === true) {
      if (mediaDesktopSmall.matches) {
        frameWidthPercentage = frameWidthPercentage - 20;
      } else {
        frameWidthPercentage = frameWidthPercentage - 16;
      }
    }
    let frameWidth =
      (frameWidthPercentage *
        (window.innerWidth ||
          document.documentElement.clientWidth ||
          document.body.clientWidth)) /
      100;

    return Math.trunc(frameWidth);
  }, [location, props.category, state.leftPanelOpen]);

  /**
   * Users own customized workbook named as [originalName]_, first time we will use the name
   * to same a copy under tableau project "Customer Reports", after that, the save operation
   * will just update 'last' view of the workbook
   */
  const handleSaveClick = () => {
    if ( state.workbook ) {
      if (getWorkbookName(state.workbook).endsWith('_')) {
        state.workbook.removeCustomViewAsync('last').then(
          (res) => {
            state.workbook.rememberCustomViewAsync('last').then(
              (res) => { 
                // Send Clarity event for report save
                if (window.clarity) {
                  window.clarity('event', 'REPORT_SAVED');
                  window.clarity('set', 'workbookName', getWorkbookName(state.workbook));
                }
              },
              (err) => {
                console.log("Remember 'last' view failure." + err);
              }
            );
          },
          (err) => {
            console.error("Remove 'last' view failure." + err);
            state.workbook.rememberCustomViewAsync('last').then(
              (res) => {
                state.workbook.setActiveCustomViewAsDefaultAsync();
                // Send Clarity event for report save
                if (window.clarity) {
                  window.clarity('event', 'REPORT_SAVED');
                  window.clarity('set', 'workbookName', getWorkbookName(state.workbook));
                }
              },
              (err) => {
                console.log("Remember 'last' view failure." + err);
              }
            );
          }
        );
      } else {
        axios
          .post(AppConfig.FO_INITIAL_URL + '/init/wbcopy', {
            workbookName: getWorkbookName(state.workbook),
            targetProject: 'Custom Reports',
            targetWorkbookName: getWorkbookName(state.workbook) + '_',
            reptSource: props.reportSource
          })
          .then((res) => { })
          .catch((err) => {
            console.log('wb copy failure,' + err);
            store.dispatch({
              type: AppActionTypes.APPSTATE_SET_APP_ERROR,
              payload: 'Save Failed'
            });
          });
      }
    }
  };

  const openTableauAdHoc = useCallback((props) => {
    getUrlTicket(props.reportSource)
      .then((res) => {
        if (res) {
          res.endsWith(props.site)
            ? window.open(res, '_tableau')
            : window.open(res);

          // Send Clarity event for ad-hoc report open
          if (window.clarity) {
            window.clarity('event', 'AD_HOC_REPORT_OPENED');
            window.clarity('set', 'reportSource', props.reportSource);
          }
        }
      })
      .catch((error) => {
        catchTableauReqCancel(error, 'Server refused Ad-Hoc operation.');
      });
  }, [navigate]);

  const initViz = useCallback((vizUrl, vizWidth, vizHeight, requestId) => {
    return new Promise(function (resolve, reject) {
      const vizContainer = document.getElementById('tableauViz');
      const isLatestRequest = () => tableauReloadRequestIdRef.current === requestId;

      if (!vizContainer) {
        reject(new Error('Tableau container not found.'));
        return;
      }

      let viz = new TableauViz();
      viz.src = vizUrl + '?:embed=yes';
      viz.toolbar = 'top';
      viz.debug = false;
      viz.height = vizHeight;
      viz.width = vizWidth;
      appendVizFilters(viz);

      let settled = false;

      const onReady = async () => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);

        if (!isLatestRequest()) {
          safeDisposeViz(viz);
          resolve(viz);
          return;
        }

        // Set tracking refs synchronously — before any await — so that
        // the runtime filter effect sees the viz as loaded if React
        // re-renders (and flushes effects) during the async work below.
        requestedRuntimeFilterKeyRef.current = runtimeFilterKey;
        lastAppliedRuntimeFilterKeyRef.current = runtimeFilterKey;
        lastLoadedReportSourceRef.current = props.reportSource;

        clearTableauMetadataCacheOnly();
        await applyInitialRuntimeFilters(viz);
        setIsApplyingFilters(false);
        // Invalidate cache so detectNotAppliedFilters re-fetches filters
        // with the person_id values that were just applied at runtime.
        try {
          clearTableauMetadataCacheOnly();
          await detectNotAppliedFilters(viz);
        } catch (detectError) {
          console.log('Unable to detect not applied filters after viz ready:', detectError);
        }
        resolve(viz);
      };

      const onError = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);

        if (!isLatestRequest()) {
          safeDisposeViz(viz);
          resolve(viz);
          return;
        }

        setIsApplyingFilters(false);
        reject(new Error('Tableau viz failed to load.'));
      };

      const timeoutId = setTimeout(() => {
        if (settled) return;
        settled = true;

        if (!isLatestRequest()) {
          safeDisposeViz(viz);
          resolve(viz);
          return;
        }

        lastAppliedRuntimeFilterKeyRef.current = runtimeFilterKey;
        lastLoadedReportSourceRef.current = props.reportSource;
        setIsApplyingFilters(false);
        resolve(viz);
      }, AppConfig.TABLEAU_VIZ_TIMEOUT);

      viz.addEventListener('firstinteractive', onReady, { once: true });
      viz.addEventListener('vizerrorhandleevent', onError, { once: true });

      tableauVizRef.current = viz;
      invalidateTableauMetadataCache();
      // Only show the loading overlay when there is async filter work to do.
      // When the URL alone is sufficient, leave the viz visible so Tableau's
      // own load progress and any error UI are not hidden behind our spinner.
      if (hasAsyncFilterWork) {
        setIsApplyingFilters(true);
      }
      while (vizContainer.firstChild) {
        vizContainer.removeChild(vizContainer.firstChild);
      }
      vizContainer.appendChild(viz);

      // Send Clarity event when the report is loaded
      if (window.clarity) {
        window.clarity('event', 'REPORT_LOADED');
        window.clarity('set', 'reportUrl', vizUrl);
      }
      
      updateState({
        viz: viz
      });
    });
  }, [appendVizFilters, applyInitialRuntimeFilters, props.reportSource, runtimeFilterKey, detectNotAppliedFilters, invalidateTableauMetadataCache, hasAsyncFilterWork]);

  createTableauRequestRef.current = () => {
    let frameWidth = calculateVizWidth();
    const requestId = tableauReloadRequestIdRef.current + 1;
    tableauReloadRequestIdRef.current = requestId;

    if (
      props?.reportSource &&
      props.reportSource.indexOf('trusted') > 0
    ) {
      lastAppliedRuntimeFilterKeyRef.current = null;
      lastLoadedReportSourceRef.current = null;
      isApplyingRuntimeFiltersRef.current = false;
      requestedRuntimeFilterKeyRef.current = null;
      if (hasAsyncFilterWork) {
        setIsApplyingFilters(true);
      }
      getUrlTicket(props.reportSource)
        .then((res) => {
          if (tableauReloadRequestIdRef.current !== requestId) {
            return;
          }

          if (res) {
            if (is503Error(res)) {
              setIsApplyingFilters(false);
              store.dispatch({
                type: AppActionTypes.APPSTATE_SET_APP_ERROR,
                payload: 'Tableau Service Unavailable.'
              });
              // Send Clarity event for report error
              if (window.clarity) {
                window.clarity("event", "REPORT_ERROR");
                window.clarity("set", "errorType", "SERVICE_UNAVAILABLE");
              }
              return;
            }
            updateState({ reportUrl: res });
            safeDisposeViz(tableauVizRef.current);
            tableauVizRef.current = null;
            invalidateTableauMetadataCache();
            updateState({
              currentDashboard: null,
              dashboards: null,
              workbook: null,
              viz: null
            });
            initViz(
              res,
              frameWidth,
              AppConfig.TABLEAU_REPORT_HEIGHT,
              requestId
            )
              .then(
                (r) => {
                  if (tableauReloadRequestIdRef.current !== requestId) {
                    safeDisposeViz(r);
                    return;
                  }

                  const workbook = r?.workbook;
                  if (!workbook) {
                    console.log('Cannot get viz workbook from Tableau viz instance.');
                    safeDisposeViz(r);
                    return;
                  }

                  let allSheets = [];
                  try {
                    allSheets = Array.isArray(workbook.publishedSheetsInfo)
                      ? workbook.publishedSheetsInfo
                      : (workbook.getPublishedSheetsInfo?.() || []);
                  } catch (sheetError) {
                    allSheets = [];
                  }

                  let myDashboards = allSheets.filter(
                    (s) => (typeof s.getSheetType === 'function' ? s.getSheetType() : s.sheetType) === 'dashboard'
                  );
                  const hiding = allSheets.filter(
                    (d) => {
                      const sheetName = d.name || d.$0?.name || '';
                      return sheetName.toUpperCase().indexOf('HIDING') !== -1;
                    }
                  );

                  if (Array.isArray(hiding) && hiding.length === 1 && Array.isArray(myDashboards)) {
                    const hidingSheetName = hiding[0].name || hiding[0].$0?.name || '';
                    let hidingDashboards = (hidingSheetName.split(':')[1] || '')
                      .split(',');
                    myDashboards = myDashboards.filter(
                      (m) => {
                        const mName = m.name || m.$0?.name || '';
                        return hidingDashboards.indexOf(mName) === -1 &&
                          mName.toUpperCase().indexOf('HIDING') === -1;
                      }
                    );
                  }
                  const parametersPromise = typeof workbook?.getParametersAsync === 'function'
                    ? workbook.getParametersAsync()
                    : Promise.resolve([]);

                  parametersPromise.then(
                    () => {
                      if (tableauReloadRequestIdRef.current !== requestId) {
                        return;
                      }

                      updateState({
                        currentDashboard:
                          Array.isArray(myDashboards) && myDashboards.length
                            ? myDashboards[0]
                            : null,
                        dashboards: myDashboards,
                        workbookTitle: getWorkbookName(workbook),
                        workbook,
                        vizWidth: frameWidth
                      });
                    },
                    (e) => console.log('get wb parameters:', e)
                  );
                },
                (err) => {
                  if (tableauReloadRequestIdRef.current === requestId) {
                    setIsApplyingFilters(false);
                  }
                  console.log('Cannot get viz ', err);
                }
              )
              .catch((err) => {
                if (tableauReloadRequestIdRef.current === requestId) {
                  setIsApplyingFilters(false);
                }
                console.log('load workbook err:' + err);
              });
          } else {
            setIsApplyingFilters(false);
          }
        })
        .catch((error) => {
          if (tableauReloadRequestIdRef.current === requestId) {
            setIsApplyingFilters(false);
          }
          catchTableauReqCancel(error, 'No Content Available');
          // Send Clarity event for report error
          if (window.clarity) {
            window.clarity("event", "REPORT_ERROR");
            window.clarity("set", "errorType", "UNKNOWN_ERROR");
          }
        });
    }
  };

  const createTableau = useMemo(() => debounce(() => {
    store.dispatch(setNotAppliedFilters([]));
    createTableauRequestRef.current?.();
  }, AppConfig.DEBOUNCE_WAIT_TIME), [props.reportSource]);

  useEffect(() => {
    if (
      props?.reportSource?.endsWith(props.site) &&
      AD_HOC_TAB_OPEN === false &&
      JUST_ENTER === true
    ) {
      AD_HOC_TAB_OPEN = true;
      openTableauAdHoc(props);
    }
  }, [props.reportSource, props.site]);

  useEffect(() => {
    return () => {
      AD_HOC_TAB_OPEN = false;
      JUST_ENTER = true;
      store.dispatch(setNotAppliedFilters([]));
    };
  }, []);

  useEffect(() => {
    createTableau();

    return () => {
      createTableau.cancel?.();
    };
  }, [createTableau]);

  const debouncedApplyRuntimeFilters = useMemo(
    () =>
      debounce(async (viz, keyAtSchedule, reportSourceAtSchedule) => {
        if (!viz?.workbook) {
          return;
        }

        // If newer changes arrived after this debounce was scheduled, drop
        // this run — the newer call's trailing-edge invocation will cover us.
        if (requestedRuntimeFilterKeyRef.current !== keyAtSchedule) {
          return;
        }

        // If an apply is already in progress (e.g. the initial-load apply
        // from initViz, or a deferred apply started from a previous finally
        // block), record this key so the in-flight run picks it up after it
        // finishes instead of dropping it.
        if (isApplyingRuntimeFiltersRef.current) {
          pendingRuntimeFilterKeyRef.current = keyAtSchedule;
          return;
        }

        isApplyingRuntimeFiltersRef.current = true;
        pendingRuntimeFilterKeyRef.current = null;
        // Flush stale cached metadata so applyRuntimeFilters queries the
        // current Tableau workbook/activeSheet state rather than reusing
        // references that may have been captured during initViz.
        tableauMetadataCacheRef.current = null;

        let applied = false;
        try {
          // Abort if the viz element was replaced by a concurrent initViz.
          if (viz !== tableauVizRef.current) {
            return;
          }
          await applyRuntimeFilters(viz);
          clearTableauMetadataCacheOnly();
          await detectNotAppliedFilters(viz);
          lastLoadedReportSourceRef.current = reportSourceAtSchedule;
          applied = true;
        } catch (error) {
          console.log('Unable to apply Tableau runtime filters:', error);
        } finally {
          if (applied) {
            lastAppliedRuntimeFilterKeyRef.current = keyAtSchedule;
          }
          isApplyingRuntimeFiltersRef.current = false;

          // A newer runtimeFilterKey was recorded while we were applying.
          // Use the latest applyRuntimeFilters (via ref) so the correct
          // filter plan — built from the most recent props — is applied.
          const pending = pendingRuntimeFilterKeyRef.current;
          if (
            pending
            && pending !== keyAtSchedule
            && pending !== lastAppliedRuntimeFilterKeyRef.current
            && viz?.workbook
            && viz === tableauVizRef.current
          ) {
            pendingRuntimeFilterKeyRef.current = null;
            requestedRuntimeFilterKeyRef.current = pending;
            isApplyingRuntimeFiltersRef.current = true;
            tableauMetadataCacheRef.current = null;
            let deferredApplied = false;
            try {
              await latestApplyRuntimeFiltersRef.current(viz);
              clearTableauMetadataCacheOnly();
              await detectNotAppliedFilters(viz);
              lastLoadedReportSourceRef.current = reportSourceAtSchedule;
              deferredApplied = true;
            } catch (err) {
              console.log('Unable to apply deferred Tableau runtime filters:', err);
            } finally {
              if (deferredApplied) {
                lastAppliedRuntimeFilterKeyRef.current = pending;
              }
              isApplyingRuntimeFiltersRef.current = false;
            }
          }
        }
      }, RUNTIME_FILTER_DEBOUNCE_MS),
    [applyRuntimeFilters, clearTableauMetadataCacheOnly, detectNotAppliedFilters]
  );

  useEffect(() => {
    return () => {
      debouncedApplyRuntimeFilters.cancel?.();
    };
  }, [debouncedApplyRuntimeFilters]);

  useEffect(() => {
    const viz = tableauVizRef.current;

    if (
      !viz?.workbook
      || !props?.reportSource
      || props.reportSource.indexOf('trusted') <= 0
      || lastLoadedReportSourceRef.current !== props.reportSource
      || requestedRuntimeFilterKeyRef.current === runtimeFilterKey
      || lastAppliedRuntimeFilterKeyRef.current === runtimeFilterKey
    ) {
      return;
    }

    // Mark this key as the latest request now (not inside the debounced
    // callback) so that intervening renders short-circuit on the guards above
    // and so the debounced callback can detect whether a newer key
    // superseded it before its timer fires.
    requestedRuntimeFilterKeyRef.current = runtimeFilterKey;
    debouncedApplyRuntimeFilters(viz, runtimeFilterKey, props.reportSource);
  }, [debouncedApplyRuntimeFilters, props.reportSource, runtimeFilterKey]);

  JUST_ENTER = false;
  if (
    props?.reportSource?.endsWith(props.site) &&
    AD_HOC_TAB_OPEN === false
  ) {
    AD_HOC_TAB_OPEN = true;
    openTableauAdHoc(props);
  }

  if (!props?.reportSource?.endsWith(props.site)) {
    let frameWidth = calculateVizWidth();

    if (state.workbook && frameWidth !== state.vizWidth) {
      try {
        // Keep resize logic on the viz frame only. Some Tableau embedding API
        // versions reject legacy SheetSizeBehavior enum values and throw.
        if (typeof state.viz?.setFrameSize === 'function') {
          state.viz.setFrameSize(frameWidth, AppConfig.TABLEAU_REPORT_HEIGHT);
        } else if (state.viz) {
          // Tableau embedding v3 exposes size as element properties.
          state.viz.width = frameWidth;
          state.viz.height = AppConfig.TABLEAU_REPORT_HEIGHT;
        }
      } catch (error) {
        console.log(`Resize fallback failed`, {
          reportSource: props.reportSource,
          frameWidth,
          error,
        });
      }
      updateState({ vizWidth: frameWidth });
    }
  }
  return (
    <>
      <div
        className="d-flex w-100"
        style={{
          position: 'relative',
          minHeight: '100%',
          overflow: isApplyingFilters ? 'hidden' : 'visible'
        }}>
        <div
          className="flex-fill reports-content"
          style={{
            minHeight: '100%',
            overflow: isApplyingFilters ? 'hidden' : 'visible'
          }}>
          <div className="iframe-full-width" ref={vizContainerDiv}>
            <div id="tableauViz" style={{ visibility: isApplyingFilters ? 'hidden' : 'visible' }}></div>
          </div>
        </div>
        <ReportRefreshOverlay open={isApplyingFilters} />
      </div>
    </>
  );
}

const mapStateToPropsSelector = createStructuredSelector({
  appliedGlobalFilters: (state) => state.globalFilters?.applied,
  permissions: (state) => state.user.permissions,
  site: (state) => state.user.currentTenantCd,
  traits: (state) => state.fo.inspection
});

export default connect(mapStateToPropsSelector)(Report);
